import React, { useCallback, useMemo, useState } from 'react';

import { getThemeColor } from '@mantine/core';
import { createPortal } from 'react-dom';
import { IconGripVertical } from 'ui/Icons';

import { ThemeIcon } from '../../../DataDisplay/ThemeIcon';
import { Box } from '../../../Miscellaneous/Box';
import { cx, rem, useMantineTheme } from '../../../Provider/Provider';
import classes from './DragAndDrop.module.css';
import type { DragHandleProps, DropIndicatorProps, DropTargetProps } from './drag-and-drop.types';
import { useDragSource } from './useDragSource';
import { useDropTarget } from './useDropTarget';

// Default constants
const DEFAULT_THICKNESS = 3;
const DEFAULT_GAP = 8;
const DOT_SIZE = 8;
const DEFAULT_PREVIEW_OPACITY = 0.8;

/**
 * Makes any content a drop target for drag and drop operations
 */
export const DropTarget = ({
  itemId,
  index,
  children,
  className,
  classNames,
  style,
  styles,
  disabled = false,
  dropIndicatorColor,
  dropIndicatorThickness = DEFAULT_THICKNESS,
  dropIndicatorGap = DEFAULT_GAP,
  dropIndicatorHorizontalOffset,
}: DropTargetProps) => {
  const { dropProps, isOver, closestEdge, dropRef } = useDropTarget(itemId, index);

  return (
    <Box
      ref={dropRef as React.RefObject<HTMLDivElement>}
      className={cx(classes.DropTarget, className)}
      style={style}
      mod={{
        'item-id': itemId,
        index: index,
        'is-over': isOver,
        'closest-edge': closestEdge,
        disabled: disabled,
        'drop-target': !disabled,
      }}
      {...(!disabled ? dropProps : {})}
    >
      {children}
      {isOver && closestEdge && !disabled && (
        <DropIndicator
          edge={closestEdge}
          color={dropIndicatorColor}
          thickness={dropIndicatorThickness}
          gap={dropIndicatorGap}
          horizontalOffset={dropIndicatorHorizontalOffset}
          classNames={classNames}
          styles={styles}
        />
      )}
    </Box>
  );
};

/**
 * Creates a draggable handle with enhanced preview
 */
export const DragHandle = ({
  itemId,
  index,
  children,
  className,
  style,
  disabled = false,
  'aria-label': ariaLabel,
  previewOpacity = DEFAULT_PREVIEW_OPACITY,
  previewClassName,
  previewStyle,
  customPreviewRenderer,
  previewElement,
  renderPreview,
}: DragHandleProps) => {
  const [previewPortalState, setPreviewPortalState] = useState<{
    show: boolean;
    container: HTMLElement | null;
    dimensions: { width: number; height: number } | null;
  }>({ show: false, container: null, dimensions: null });

  // Create a minimal native preview renderer that enables our Portal
  const minimalPreviewRenderer = useCallback(
    (container: HTMLElement) => {
      // Get dimensions if previewElement is provided
      let dimensions: { width: number; height: number } | null = null;
      if (previewElement) {
        const element = typeof previewElement === 'function' ? previewElement() : previewElement;
        if (element) {
          const rect = element.getBoundingClientRect();
          dimensions = { width: rect.width, height: rect.height };
        }
      }

      // Create an invisible element for the native preview
      const invisible = document.createElement('div');
      invisible.style.width = '1px';
      invisible.style.height = '1px';
      invisible.style.opacity = '0';
      container.appendChild(invisible);

      // Enable our Portal-based preview
      setPreviewPortalState({ show: true, container, dimensions });

      return () => {
        setPreviewPortalState({ show: false, container: null, dimensions: null });
        if (container.contains(invisible)) {
          container.removeChild(invisible);
        }
      };
    },
    [previewElement]
  );

  // Use the useDragSource hook with preview
  const { dragProps, isDragging, canDrag, dragRef } = useDragSource(itemId, index, {
    customPreviewRenderer: customPreviewRenderer || minimalPreviewRenderer,
    previewElement,
  });

  const defaultAriaLabel = ariaLabel || `Drag handle for item ${itemId}`;

  return (
    <>
      <div
        ref={dragRef as React.RefObject<HTMLDivElement>}
        className={className}
        style={{
          cursor: canDrag && !disabled ? 'grab' : 'default',
          userSelect: 'none',
          touchAction: 'none',
          opacity: isDragging ? 0.5 : 1,
          ...style,
        }}
        aria-label={defaultAriaLabel}
        data-item-id={itemId}
        data-index={index}
        data-is-dragging={isDragging}
        data-can-drag={canDrag}
        data-disabled={disabled}
        {...(!disabled && canDrag ? dragProps : {})}
      >
        {children !== undefined && children !== null ? (
          children
        ) : (
          <ThemeIcon variant="transparent" color="gray.4">
            <IconGripVertical size={20} brand="tabler" />
          </ThemeIcon>
        )}

        {/* Hidden instructions for screen readers */}
        <div
          id={`drag-instructions-${itemId}`}
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          Press space or enter to start dragging. Use arrow keys to move. Press space or enter to
          drop.
        </div>
      </div>

      {/* React Portal preview */}
      {previewPortalState.show &&
        previewPortalState.container &&
        createPortal(
          renderPreview ? (
            // Use renderPreview if provided
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: `rgba(255, 255, 255, ${previewOpacity})`,
                border: '1px solid #e1e5e9',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '200px',
                maxWidth: '400px',
                ...previewStyle,
              }}
              className={previewClassName}
            >
              {renderPreview({
                width: previewPortalState.dimensions?.width,
                height: previewPortalState.dimensions?.height,
              })}
            </div>
          ) : (
            // Simple fallback preview
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: `rgba(255, 255, 255, ${previewOpacity})`,
                border: '1px solid #e1e5e9',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: '14px',
                color: '#495057',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                ...previewStyle,
              }}
              className={previewClassName}
            >
              Moving item {index + 1}
            </div>
          ),
          previewPortalState.container
        )}
    </>
  );
};

/**
 * Visual indicator showing where a dragged item will be dropped
 */
export const DropIndicator = ({
  edge,
  color = 'cobalt.6',
  thickness = DEFAULT_THICKNESS,
  gap = DEFAULT_GAP,
  horizontalOffset,
  classNames,
  styles,
}: DropIndicatorProps) => {
  const theme = useMantineTheme();
  const isHorizontal = edge === 'left' || edge === 'right';
  const isVertical = edge === 'top' || edge === 'bottom';

  // Calculate the actual horizontal offset (defaults to gap/2)
  const calculatedHorizontalOffset = useMemo(
    () => horizontalOffset ?? gap / 2,
    [horizontalOffset, gap]
  );

  return (
    <Box
      className={cx(classes.DropIndicator, classNames?.indicator)}
      data-edge={edge}
      style={{
        '--mantine-dnd-drop-indicator-thickness': rem(thickness),
        '--mantine-dnd-drop-indicator-gap': rem(gap),
        '--mantine-dnd-drop-indicator-horizontal-offset': rem(calculatedHorizontalOffset),
        '--mantine-dnd-drop-indicator-color': getThemeColor(color, theme),
        '--mantine-dnd-drop-indicator-dot-size': rem(DOT_SIZE),
        ...styles?.indicator,
      }}
      aria-hidden="true"
    >
      {/* First dot */}
      <Box
        className={cx(classes.DropIndicator__Dot, classNames?.dot)}
        mod={{
          left: isVertical,
          top: isHorizontal,
        }}
        style={styles?.dot}
      />

      {/* Line */}
      <Box className={cx(classes.DropIndicator__Line, classNames?.line)} style={styles?.line} />

      {/* Second dot */}
      <Box
        className={cx(classes.DropIndicator__Dot, classNames?.dot)}
        mod={{
          right: isVertical,
          bottom: isHorizontal,
        }}
        style={styles?.dot}
      />
    </Box>
  );
};
